class PageHeaderSection extends HTMLElement {
  constructor() {
    super();

    this.isSticky = this.dataset.sticky === "true";
    this.isStickyAlways = this.dataset.stickyAlways === "true";
    this.isTransparent = this.dataset.transparent === "true";
    this.isAlwaysMobileMenu = this.dataset.alwaysMobileMenu === "true";

    // Optional CSS selector for the section over which the header should hide
    this.hideOverSelector = this.dataset.hideOverSelector || null;

    this.classBodyAlwaysMobileMenu = "mobile-nav";

    this.classHeaderAlwaysVisible = "sticky-always";

    this.header = document.querySelector(".page-header");

    // Determine sticky-always mode from multiple sources. The Liquid template
    // may annotate the header element with data-sticky-always or the class
    // wt-header--sticky-always; the section element (this.dataset) might also
    // contain the flag. Also check globally for these markers to be robust in
    // different template structures.
    try {
      const fromSection = this.dataset && this.dataset.stickyAlways === "true";
      const fromHeader = this.header && ((this.header.dataset && this.header.dataset.stickyAlways === "true") || this.header.classList.contains("wt-header--sticky-always"));
      const fromGlobalAttr = !!document.querySelector('[data-sticky-always="true"]');
      const fromGlobalClass = !!document.querySelector('.wt-header--sticky-always');
      this.isStickyAlways = !!(fromSection || fromHeader || fromGlobalAttr || fromGlobalClass);
    } catch (e) {
      // ignore DOM access errors and leave this.isStickyAlways as originally set
    }

    this.desktopMenuTrigger = document.querySelector(
      ".wt-header__sticky-menu-trigger",
    );
    this.desktopMenuBar = document.querySelector(".wt-drawer--nav");

    this.enabledClass = "sticky-enabled";
    this.showClass = "sticky-show";
    this.desktopHeaderWithMenuBarClass = "page-header--sticky-show-menubar-lg";
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.disableStickyHeader();
  }

  getHeaderHeight() {
    return this.header ? this.header.offsetHeight : 0;
  }

  /** Returns the target element for the hide-over behavior (if any). */
  getHideOverTarget() {
    if (!this.hideOverSelector) return null;
    if (this._hideOverTarget && document.body.contains(this._hideOverTarget)) {
      return this._hideOverTarget;
    }
    this._hideOverTarget =
      document.querySelector(this.hideOverSelector) || null;
    return this._hideOverTarget;
  }

  /**
   * Should the sticky header be hidden because its bottom edge reached
   * the target section's top edge?
   *
   * Geometry reference (viewport coordinates):
   * - header top: usually 0 when sticky is shown
   * - header bottom: header.offsetHeight
   * - section top: target.getBoundingClientRect().top
   *
   * We hide when sectionTop <= headerBottom.
   */
  shouldHideOverSection() {
    const target = this.getHideOverTarget();
    if (!target || !this.header) return false;

    const rect = target.getBoundingClientRect();
    // Prefer using a stable cached header height to avoid reflow-induced oscillation
    const headerH = this._stableHeaderHeight ?? this.getHeaderHeight();

    // Small hysteresis to avoid flicker on exact boundary (in px)
    const EPS = 1;

    // True when the section intersects with the header band [0, headerH)
    // i.e. sectionTop < headerBottom && sectionBottom > 0
    const overlapsHeaderBand = rect.top < headerH - EPS && rect.bottom > EPS;

    return overlapsHeaderBand;
  }

  /** Attach / detach a lightweight resize handler to keep things in sync. */
  attachResizeHandler() {
    // Re-run calculations on resize; update cached header height and schedule a
    // processed scroll tick in the next frame to avoid measuring during layout
    // thrash (helps in iframes / mobile address-bar changes).
    this._onResize = () => {
      window.requestAnimationFrame(() => {
        this._stableHeaderHeight = this.getHeaderHeight();
        this.scrollHandler?.();
      });
    };
    window.addEventListener("resize", this._onResize, { passive: true });
  }

  detachResizeHandler() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
      this._onResize = null;
    }
  }

  enableStickyHeader() {
    if (!this.header) {
      console.error("Header element not found for enabling sticky header");
      return;
    }

    // Re-evaluate sticky-always from DOM at enable time. Some templates add
    // the data/class after element construction, so ensure we read it now.
    try {
      const headerEl = this.header || document.querySelector(".page-header");
      const fromSection = this.dataset && this.dataset.stickyAlways === "true";
      const fromHeader = headerEl && ((headerEl.dataset && headerEl.dataset.stickyAlways === "true") || headerEl.classList.contains("wt-header--sticky-always"));
      const fromGlobalAttr = !!document.querySelector('[data-sticky-always="true"]');
      const fromGlobalClass = !!document.querySelector('.wt-header--sticky-always');
      this.isStickyAlways = !!(fromSection || fromHeader || fromGlobalAttr || fromGlobalClass);
    } catch (e) {}

    document.body.classList.add("page-header-sticky");

    if (this.isStickyAlways) {
      this.header.classList.add(this.classHeaderAlwaysVisible);
    }

    let prevScrollpos = window.pageYOffset;

    const isDesktop = window.matchMedia("(min-width: 1200px)").matches;
    const isMenuBarOpen = () =>
      this.header.classList.contains(this.desktopHeaderWithMenuBarClass);
    const isHeaderWithDesktopNav =
      !document.body.classList.contains("mobile-nav");
    const allLLevelsLinks =
      this.desktopMenuBar?.querySelectorAll("a[data-menu-level]");
    const onlyLevel1Links = this.desktopMenuBar?.querySelectorAll(
      "a[data-menu-level='1']",
    );

    const header = document.querySelector("#header");

    const calculateNavbarTopMargin = () => {
      const navbar = document.querySelector("#wt-drawer-nav");
      if (!navbar || !header) return 0;

      let marginTop = 0;

      if (navbar.offsetHeight > header.offsetHeight) {
        marginTop = header.offsetHeight - navbar.offsetHeight;
      } else {
        marginTop = Math.abs(navbar.offsetHeight - header.offsetHeight);
      }

      navbar.style.setProperty("--top-margin", `${marginTop}px`);

      return marginTop;
    };

    const calculateStickyFiltersTopOffset = (value = 0) => {
      const stickyFilters = document.querySelector(
        ".collection__sticky-header",
      );
      const plpWrapper = document.querySelector(".collection-grid-section");

      if (stickyFilters && plpWrapper) {
        const offset = value ?? `${this.header.offsetHeight}px`;
        plpWrapper.style.setProperty("--filters-sticky-offset", offset);
      }
    };

    calculateNavbarTopMargin();
    calculateStickyFiltersTopOffset();

    // Cache a stable header height to avoid measuring on every scroll tick.
    // This prevents feedback loops when the header height changes (e.g. search bar collapsing).
    this._stableHeaderHeight = this.getHeaderHeight();

    // If ResizeObserver is available, watch the header for size changes and
    // update the cached height in a controlled way (minimal, optional).
    if (window.ResizeObserver) {
      // Use ResizeObserver to update cached height immediately when header
      // layout changes (search collapse/announce bar). We read contentRect
      // height directly to avoid forcing an extra reflow.
      this._headerResizeObserver = new ResizeObserver((entries) => {
        const entry = entries && entries[0];
        if (entry && entry.contentRect && entry.contentRect.height) {
          this._stableHeaderHeight = entry.contentRect.height;
        } else {
          this._stableHeaderHeight = this.getHeaderHeight();
        }
        // schedule processed scroll tick in next frame to re-evaluate visibility
        window.requestAnimationFrame(() => this.scrollHandler?.());
      });
      try {
        this._headerResizeObserver.observe(this.header);
      } catch (e) {
        // noop if observe fails
      }
    }

    // Controlled sticky header operations with minimal guards to avoid
    // feedback loops when header height changes (search bar collapsing) or
    // IntersectionObserver toggles rapidly inside an iframe.
    const TOGGLE_COOLDOWN_MS = 160; // short cooldown to prevent oscillation
    let _lastToggleAt = 0;
    const stickyHeader = {
      show: (force = false) => {
        // avoid redundant work
        if (stickyHeader.visible && !force) return;
        // throttle rapid toggles
        if (!force && Date.now() - _lastToggleAt < TOGGLE_COOLDOWN_MS) return;
        if (this.header) this.header.classList.add(this.showClass);
        stickyHeader.visible = true;
        stickyHeader.handleBehavior();
        calculateNavbarTopMargin();
        calculateStickyFiltersTopOffset();
        _lastToggleAt = Date.now();
        // update stable header height after DOM changes in next frame
        window.requestAnimationFrame(() => {
          this._stableHeaderHeight = this.getHeaderHeight();
        });
      },
      hide: (force = false) => {
        // If sticky-always is enabled, never auto-hide under any scroll/observer logic
        // UNLESS force=true (e.g., shouldHideOverSection needs to override).
        if (this.isStickyAlways && !force) {
          stickyHeader.show(true);
          return;
        }

        // avoid redundant work
        if (!stickyHeader.visible && !force) return;
        // allow forced hide (e.g. shouldHideOverSection) to bypass cooldown
        if (!force && Date.now() - _lastToggleAt < TOGGLE_COOLDOWN_MS) return;
        if (this.header) this.header.classList.remove(this.showClass);
        stickyHeader.visible = false;
        stickyHeader.handleBehavior();
        calculateStickyFiltersTopOffset(0);
        _lastToggleAt = Date.now();
        window.requestAnimationFrame(() => {
          this._stableHeaderHeight = this.getHeaderHeight();
        });
      },
      enable: (force = false) => {
        if (stickyHeader.enabled && !force) return;
        if (!force && Date.now() - _lastToggleAt < TOGGLE_COOLDOWN_MS) return;
        if (this.header) this.header.classList.add(this.enabledClass);
        stickyHeader.enabled = true;
        stickyHeader.handleBehavior();
        _lastToggleAt = Date.now();
        window.requestAnimationFrame(() => {
          this._stableHeaderHeight = this.getHeaderHeight();
        });
      },
      disable: (force = false) => {
        if (!stickyHeader.enabled && !force) return;
        if (!force && Date.now() - _lastToggleAt < TOGGLE_COOLDOWN_MS) return;
        // If sticky-always is enabled, do not remove the visible state (showClass).
        // Keep header visible but still mark sticky as disabled for behavior purposes.
        if (this.isStickyAlways) {
          if (this.header) this.header.classList.remove(this.enabledClass);
          stickyHeader.enabled = false;
          stickyHeader.handleBehavior();
          _lastToggleAt = Date.now();
          window.requestAnimationFrame(() => {
            this._stableHeaderHeight = this.getHeaderHeight();
          });
          return;
        }
        if (this.header)
          this.header.classList.remove(this.enabledClass, this.showClass);
        stickyHeader.enabled = false;
        stickyHeader.handleBehavior();
        _lastToggleAt = Date.now();
        window.requestAnimationFrame(() => {
          this._stableHeaderHeight = this.getHeaderHeight();
        });
      },
      enabled: false,
      visible: true,
      handleBehavior: () => {
        if (isHeaderWithDesktopNav && isDesktop && this.header) {
          stickyHeader.log();
          if (!isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "-1");
          }
          if (isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "0");
          }
          if (!stickyHeader.enabled) {
            setTabindex(onlyLevel1Links, "0");
            if (this.desktopMenuTrigger)
              setTabindex([this.desktopMenuTrigger], "-1");
          } else if (this.desktopMenuTrigger)
            setTabindex([this.desktopMenuTrigger], "0");
        }
      },
      log: () => {},
    };

    // --- Scroll handler with "hide-over-section" override ----------
    // Use rAF-throttled processing, prefer visualViewport when available, and
    // add a small hysteresis to avoid toggling on micro-jitter (esp. in iframes).
    // Detect iframe environment; used to apply tighter stabilization only inside iframes
    let isInIframe = false;
    try {
      isInIframe = window.self !== window.top;
    } catch (e) {
      // If check fails (e.g., cross-origin or CSP), default to normal browsing mode
      // since that's the common case and provides better UX than iframe workarounds.
      isInIframe = false;
    }

    const IO_LOCK_MS = isInIframe ? 220 : 0; // lock observer flips briefly in iframe
    let _ioLockUntil = 0;

    // Choose the most stable scroll source depending on environment.
    // In iframes, document.scrollingElement.scrollTop is often more reliable
    // than window.pageYOffset; otherwise prefer visualViewport.pageTop.
    const readScrollPos = () => {
      // debug: report which source is used
      try {
        if (isInIframe) {
          const se = document.scrollingElement;
          if (se && typeof se.scrollTop === "number") {
            if (window.__PH_DEBUG)
              console.debug("[PH] scrollSource=scrollingElement", se.scrollTop);
            return se.scrollTop;
          }
          if (
            document.documentElement &&
            typeof document.documentElement.scrollTop === "number"
          ) {
            if (window.__PH_DEBUG)
              console.debug(
                "[PH] scrollSource=documentElement",
                document.documentElement.scrollTop,
              );
            return document.documentElement.scrollTop;
          }
          if (
            window.visualViewport &&
            typeof window.visualViewport.pageTop === "number"
          ) {
            if (window.__PH_DEBUG)
              console.debug(
                "[PH] scrollSource=visualViewport",
                window.visualViewport.pageTop,
              );
            return window.visualViewport.pageTop;
          }
          if (window.__PH_DEBUG)
            console.debug("[PH] scrollSource=pageYOffset", window.pageYOffset);
          return window.pageYOffset;
        } else {
          if (
            window.visualViewport &&
            typeof window.visualViewport.pageTop === "number"
          ) {
            if (window.__PH_DEBUG)
              console.debug(
                "[PH] scrollSource=visualViewport",
                window.visualViewport.pageTop,
              );
            return window.visualViewport.pageTop;
          }
          const se = document.scrollingElement;
          if (se && typeof se.scrollTop === "number") {
            if (window.__PH_DEBUG)
              console.debug("[PH] scrollSource=scrollingElement", se.scrollTop);
            return se.scrollTop;
          }
          if (window.__PH_DEBUG)
            console.debug("[PH] scrollSource=pageYOffset", window.pageYOffset);
          return window.pageYOffset;
        }
      } catch (e) {
        return window.pageYOffset;
      }
    };

    // initialize prevScrollpos from chosen source
    prevScrollpos = readScrollPos();

    let _ticking = false;
    const HIDE_THRESHOLD = 8; // px scrolled down before hiding
    const SHOW_THRESHOLD = 6; // px scrolled up before showing
    const JITTER_EPS = 2; // ignore tiny deltas
    const FORCE_HIDE_DELTA = 48; // large per-frame delta forces hide even during cooldown

    const _onScrollFrame = () => {
      _ticking = false;
      const currentScrollPos = readScrollPos();
      const delta = currentScrollPos - prevScrollpos;

      // Check shouldHideOverSection FIRST - it overrides even sticky-always mode.
      // This ensures header hides over target sections (e.g., .collection__grid)
      // regardless of sticky-always setting.
      if (this.shouldHideOverSection()) {
        // force hide when overlapping target section; bypass cooldown and sticky-always
        stickyHeader.hide(true);
      } else if (this.isStickyAlways) {
        // If sticky-always mode is enabled and NOT over hide-target, keep visible
        stickyHeader.show();
        prevScrollpos = currentScrollPos;
        return;
      } else {
        // Original behavior preserved, but with hysteresis checks
        // ignore tiny jitter
        if (Math.abs(delta) <= JITTER_EPS) {
          // do nothing, keep current state
        } else if (delta > HIDE_THRESHOLD) {
          // scrolled down significantly
          // allow a very large fast-scroll delta to bypass any recent cooldown
          if (
            Date.now() - _lastToggleAt < TOGGLE_COOLDOWN_MS &&
            delta < FORCE_HIDE_DELTA
          ) {
            // still within cooldown and not a large fast swipe; skip hide
          } else {
            stickyHeader.hide();
          }
        } else if (delta < -SHOW_THRESHOLD) {
          // scrolled up significantly
          stickyHeader.show();
        }
        // else: within hysteresis, preserve existing visibility
      }

      prevScrollpos = currentScrollPos;
    };

    // rAF wrapper exposed as this.scrollHandler so removal logic still works
    this.scrollHandler = () => {
      if (_ticking) return;
      _ticking = true;
      window.requestAnimationFrame(_onScrollFrame);
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    this.attachResizeHandler();

    this.desktopMenuTrigger?.addEventListener("click", (e) => {
      e.preventDefault();
      this.desktopMenuBar?.classList.toggle("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.toggle(
        "wt-header__sticky-menu-trigger--active",
      );
      this.header?.classList.toggle(this.desktopHeaderWithMenuBarClass);
      stickyHeader.handleBehavior();
    });

    const sentinel = document.querySelector(".sticky-header__threshold");
    const handleStickySentinel = (entries) => {
      entries.forEach(({ isIntersecting, boundingClientRect, rootBounds }) => {
        // If we're in an iframe, briefly lock observer-driven toggles to avoid
        // oscillation at the switch boundary caused by layout/height jitter.
        if (isInIframe && Date.now() < _ioLockUntil) {
          if (window.__PH_DEBUG)
            console.debug(
              "[PH] IO skipped due to lock",
              Date.now(),
              _ioLockUntil,
              { isIntersecting, top: boundingClientRect?.top },
            );
          return;
        }

        if (isIntersecting) {
          // allow observer to force disable (bypass cooldown) to avoid missed transitions
          stickyHeader.disable(true);
        } else {
          stickyHeader.enable(true);
        }

        // after observer-driven change, lock further observer actions for a short window
        if (isInIframe) _ioLockUntil = Date.now() + IO_LOCK_MS;
      });
    };

    this.stickyHeaderObserver = new IntersectionObserver(handleStickySentinel, {
      root: null,
      rootMargin: `${this.isStickyAlways ? "-160" : "-100"}px 0px 0px 0px`,
      threshold: 0,
    });
    if (sentinel) this.stickyHeaderObserver.observe(sentinel);
  }

  disableStickyHeader() {
    if (this.header) {
      // Remove classes added by sticky header
      this.header.classList.remove(
        this.enabledClass,
        this.showClass,
        this.desktopHeaderWithMenuBarClass,
      );
      document.body.classList.remove("page-header-sticky");
      this.desktopMenuBar?.classList.remove("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.remove(
        "wt-header__sticky-menu-trigger--active",
      );
      if (!this.isStickyAlways) {
        this.header.classList.remove(this.classHeaderAlwaysVisible);
      }
    }

    // Remove the scroll event listener
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }

    // Detach resize handler
    this.detachResizeHandler();

    // Disconnect the IntersectionObserver
    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
      this.stickyHeaderObserver = null;
    }

    // disconnect optional ResizeObserver if created
    if (this._headerResizeObserver) {
      try {
        this._headerResizeObserver.disconnect();
      } catch (e) {}
      this._headerResizeObserver = null;
    }
  }

  init() {
    if (this.isSticky) {
      this.enableStickyHeader();
    } else {
      this.disableStickyHeader();
    }
  }
}

customElements.define("page-header", PageHeaderSection);